import { useMemo, useRef, useState } from 'react';
import { UMIcon, UMInput } from '../../urbanmap/_components';
import { UMR, caption } from './tokens';
import type { Commune } from './types';

interface Props {
  communes: Commune[];
  /** Déplace la carte sur la commune choisie, sans l'ajouter au périmètre. */
  onPick: (insee: string) => void;
}

const MAX_SUGGESTIONS = 6;
const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const postalCodes = (commune: Commune) => commune.insee === '35238'
  ? ['35000', '35200', '35700']
  : commune.postalCode ? [commune.postalCode] : [];

/** Recherche de lieu : recentre la carte avant de dessiner un contour. */
export function PlaceSearch({ communes, onPick }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout>>();

  const suggestions = useMemo(() => {
    const q = norm(query.trim());
    if (!q) return communes.slice(0, MAX_SUGGESTIONS);
    return communes
      .filter((c) => norm(c.name).includes(q) || postalCodes(c).some((code) => code.startsWith(q)) || c.insee.startsWith(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [communes, query]);

  const pick = (c: Commune) => {
    onPick(c.insee);
    setQuery(c.name);
    setOpen(false);
  };

  return (
    <div
      style={{ position: 'relative' }}
      onFocus={() => { clearTimeout(blurTimer.current); setOpen(true); }}
      onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 120); }}
    >
      <UMInput
        value={query}
        onChange={(v) => { setQuery(v); setOpen(true); }}
        placeholder="Aller à une commune, un code postal…"
        ariaLabel="Aller à un lieu sur la carte"
        icon="my_location"
      />
      {open && suggestions.length > 0 && (
        <ul
          role="listbox"
          aria-label="Lieux proposés"
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, margin: 0, padding: 0, listStyle: 'none',
            background: UMR.white, border: `1px solid ${UMR.borderInput}`, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxHeight: 232, overflowY: 'auto',
          }}
        >
          {suggestions.map((c) => (
            <li key={c.insee}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(c)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer',
                  border: 'none', borderBottom: `1px solid ${UMR.borderSubtle}`, background: 'transparent', textAlign: 'left', fontFamily: UMR.font, fontSize: 14,
                }}
              >
                <UMIcon name="place" size={18} color={UMR.textSecondary} />
                <span style={{ flex: 1 }}>{c.name} <span style={{ color: UMR.textSecondary }}>({postalCodes(c).join(', ') || c.insee})</span></span>
                <span style={{ ...caption, whiteSpace: 'nowrap' }}>Aller</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
